import type { Dispatch } from "react";
import { flushSync } from "react-dom";
import type { DialogHandle, DialogOutcome, DialogRequest } from "@tc-superpods/app-contract";
import type { ShellAction } from "./state";
import type { DialogEntry, DialogView } from "./types";

export interface DialogController {
  open<TInput, TResult>(
    ownerAppId: string,
    request: DialogRequest<TInput, TResult>
  ): Promise<DialogOutcome<TResult>>;
  setHostElement(id: string, element: HTMLElement | null): void;
  mountPending(): void;
  handleEscape(event: KeyboardEvent): void;
  dismissDialog(id: string): void;
  dismissTopFromBackdrop(): void;
  closeDialogsForApp(ownerAppId: string): Promise<void>;
  getTopDialog(): DialogEntry | null;
}

export function createDialogController(dispatch: Dispatch<ShellAction>): DialogController {
  const entries = new Map<string, DialogEntry>();
  const order: string[] = [];
  const mounting = new Set<string>();
  let sequence = 0;

  function toView(entry: DialogEntry): DialogView {
    return {
      id: entry.id,
      ownerAppId: entry.ownerAppId,
      title: entry.request.title,
      size: entry.request.size ?? "md",
      dismissible: entry.request.dismissible !== false
    };
  }

  function getTopDialog(): DialogEntry | null {
    const topId = order.at(-1);

    return topId ? (entries.get(topId) ?? null) : null;
  }

  function open<TInput, TResult>(
    ownerAppId: string,
    request: DialogRequest<TInput, TResult>
  ): Promise<DialogOutcome<TResult>> {
    return new Promise<DialogOutcome<TResult>>((resolve) => {
      const id = `dialog-${(sequence += 1)}`;
      const entry: DialogEntry = {
        id,
        ownerAppId,
        request: request as DialogRequest<unknown, unknown>,
        resolve: (outcome) => {
          resolve(outcome as DialogOutcome<TResult>);
        },
        hostElement: null,
        previousActiveElement:
          document.activeElement instanceof HTMLElement ? document.activeElement : null,
        mounted: false,
        closing: false,
        settled: false
      };

      entries.set(id, entry);
      order.push(id);
      flushSync(() => {
        dispatch({ type: "dialog-add", dialog: toView(entry) });
      });
    });
  }

  function setHostElement(id: string, element: HTMLElement | null): void {
    const entry = entries.get(id);

    if (entry) {
      entry.hostElement = element;
    }
  }

  function mountPending(): void {
    for (const entry of entries.values()) {
      if (entry.mounted || entry.closing || mounting.has(entry.id) || !entry.hostElement) {
        continue;
      }

      void mountDialog(entry);
    }
  }

  async function mountDialog(entry: DialogEntry): Promise<void> {
    const hostElement = entry.hostElement;

    if (!hostElement) {
      return;
    }

    mounting.add(entry.id);

    const handle: DialogHandle<unknown> = {
      close: (result) => {
        void closeDialog(entry.id, { result, dismissed: false });
      },
      dismiss: () => {
        void closeDialog(entry.id, { dismissed: true });
      }
    };

    try {
      await entry.request.mount(hostElement, handle, entry.request.data);
      entry.mounted = true;
      focusDialog(entry);
    } catch (error) {
      console.error("Dialog mount failed.", error);
      await closeDialog(entry.id, { dismissed: true });
    } finally {
      mounting.delete(entry.id);
    }
  }

  async function closeDialog(id: string, outcome: DialogOutcome<unknown>): Promise<void> {
    const entry = entries.get(id);

    if (!entry || entry.closing) {
      return;
    }

    entry.closing = true;

    try {
      if (entry.mounted && entry.hostElement) {
        await entry.request.unmount(entry.hostElement);
      }
    } catch (error) {
      console.error("Dialog unmount failed.", error);
    } finally {
      entry.mounted = false;
      entry.hostElement = null;
      entries.delete(id);
      removeFromOrder(id);
      flushSync(() => {
        dispatch({ type: "dialog-remove", id });
      });
      restoreDialogFocus(entry);
      settleDialog(entry, outcome);

      const nextTopDialog = getTopDialog();
      if (nextTopDialog) {
        focusDialog(nextTopDialog);
      }
    }
  }

  async function closeDialogsForApp(ownerAppId: string): Promise<void> {
    const ownedDialogIds = order.filter((id) => entries.get(id)?.ownerAppId === ownerAppId);

    for (const dialogId of ownedDialogIds) {
      await closeDialog(dialogId, { dismissed: true });
    }
  }

  function handleEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape" || event.defaultPrevented) {
      return;
    }

    const topDialog = getTopDialog();
    if (!topDialog || topDialog.request.dismissible === false) {
      return;
    }

    event.preventDefault();
    void closeDialog(topDialog.id, { dismissed: true });
  }

  function dismissDialog(id: string): void {
    const dialog = entries.get(id);

    if (!dialog || dialog.request.dismissible === false) {
      return;
    }

    void closeDialog(id, { dismissed: true });
  }

  function dismissTopFromBackdrop(): void {
    const topDialog = getTopDialog();

    if (!topDialog || topDialog.request.dismissible === false) {
      return;
    }

    void closeDialog(topDialog.id, { dismissed: true });
  }

  function removeFromOrder(id: string): void {
    const index = order.indexOf(id);

    if (index !== -1) {
      order.splice(index, 1);
    }
  }

  return {
    open,
    setHostElement,
    mountPending,
    handleEscape,
    dismissDialog,
    dismissTopFromBackdrop,
    closeDialogsForApp,
    getTopDialog
  };
}

function focusDialog(entry: DialogEntry): void {
  const target = entry.hostElement?.querySelector<HTMLElement>(
    'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])'
  );

  target?.focus();
}

function restoreDialogFocus(entry: DialogEntry): void {
  if (entry.previousActiveElement?.isConnected) {
    entry.previousActiveElement.focus();
  }
}

function settleDialog(entry: DialogEntry, outcome: DialogOutcome<unknown>): void {
  if (entry.settled) {
    return;
  }

  entry.settled = true;
  entry.resolve(outcome);
}
